import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Plus, X, MoreVertical, Pencil, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { tacticalApi } from '../lib/api';

const MAX_SITES = 15;

const FacilitySiteTabs = ({ 
  tacticalId, 
  sites = [], 
  activeSiteId, 
  onSiteChange, 
  onSitesUpdated,
  isReadOnly = false 
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [renamingSite, setRenamingSite] = useState(null);
  const [deletingSite, setDeletingSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draggedSite, setDraggedSite] = useState(null);

  const sortedSites = [...sites].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleAddSite = async () => {
    if (!newSiteName.trim()) {
      toast.error('Please enter a site name');
      return;
    }

    setLoading(true);
    try {
      const result = await tacticalApi.addSite(tacticalId, newSiteName.trim());
      toast.success('Site added');
      setShowAddDialog(false);
      setNewSiteName('');
      onSitesUpdated();
      // Switch to the new site
      if (result.site) {
        onSiteChange(result.site.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add site');
    } finally {
      setLoading(false);
    }
  };

  const handleRenameSite = async () => {
    if (!newSiteName.trim() || !renamingSite) {
      toast.error('Please enter a site name');
      return;
    }

    setLoading(true);
    try {
      await tacticalApi.updateSite(tacticalId, renamingSite.id, { name: newSiteName.trim() });
      toast.success('Site renamed');
      setShowRenameDialog(false);
      setNewSiteName('');
      setRenamingSite(null);
      onSitesUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename site');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSite = async () => {
    if (!deletingSite) return;

    setLoading(true);
    try {
      await tacticalApi.deleteSite(tacticalId, deletingSite.id);
      toast.success('Site deleted');
      setShowDeleteDialog(false);
      setDeletingSite(null);
      onSitesUpdated();
      // If we deleted the active site, switch to the first remaining site
      if (activeSiteId === deletingSite.id && sortedSites.length > 1) {
        const remainingSites = sortedSites.filter(s => s.id !== deletingSite.id);
        if (remainingSites.length > 0) {
          onSiteChange(remainingSites[0].id);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete site');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, site) => {
    setDraggedSite(site);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetSite) => {
    e.preventDefault();
    if (!draggedSite || draggedSite.id === targetSite.id) {
      setDraggedSite(null);
      return;
    }

    // Calculate new order
    const oldIndex = sortedSites.findIndex(s => s.id === draggedSite.id);
    const newIndex = sortedSites.findIndex(s => s.id === targetSite.id);
    
    const newOrder = [...sortedSites];
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, draggedSite);

    // Send new order to server
    try {
      await tacticalApi.reorderSites(tacticalId, newOrder.map(s => s.id));
      onSitesUpdated();
    } catch (err) {
      toast.error('Failed to reorder sites');
    }
    
    setDraggedSite(null);
  };

  const openRenameDialog = (site) => {
    setRenamingSite(site);
    setNewSiteName(site.name);
    setShowRenameDialog(true);
  };

  const openDeleteDialog = (site) => {
    setDeletingSite(site);
    setShowDeleteDialog(true);
  };

  if (sites.length === 0) {
    return null;
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-1 border-b overflow-x-auto pb-1">
        {sortedSites.map((site) => (
          <div
            key={site.id}
            draggable={!isReadOnly}
            onDragStart={(e) => handleDragStart(e, site)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, site)}
            className={`
              flex items-center gap-1 px-3 py-2 rounded-t-lg cursor-pointer transition-colors
              ${activeSiteId === site.id 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted hover:bg-muted/80'}
              ${draggedSite?.id === site.id ? 'opacity-50' : ''}
            `}
          >
            {!isReadOnly && (
              <GripVertical className="w-3 h-3 opacity-50 cursor-grab" />
            )}
            <button
              onClick={() => onSiteChange(site.id)}
              className="text-sm font-medium whitespace-nowrap"
              data-testid={`site-tab-${site.id}`}
            >
              {site.name}
            </button>
            {!isReadOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="ml-1 p-0.5 rounded hover:bg-black/10">
                    <MoreVertical className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openRenameDialog(site)}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  {sites.length > 1 && (
                    <DropdownMenuItem 
                      onClick={() => openDeleteDialog(site)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ))}
        
        {!isReadOnly && sites.length < MAX_SITES && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="h-9 px-2"
            data-testid="add-site-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Add Site Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Facility Site</DialogTitle>
            <DialogDescription>
              Enter a name for the new facility site. It will have the same template questions as other sites.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Site name (e.g., NewHaven, Main Campus)"
              value={newSiteName}
              onChange={(e) => setNewSiteName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSite()}
              autoFocus
              data-testid="new-site-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSite} disabled={loading || !newSiteName.trim()}>
              {loading ? 'Adding...' : 'Add Site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Site Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Site</DialogTitle>
            <DialogDescription>
              Enter a new name for "{renamingSite?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="New site name"
              value={newSiteName}
              onChange={(e) => setNewSiteName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameSite()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameSite} disabled={loading || !newSiteName.trim()}>
              {loading ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Site Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingSite?.name}"? This action cannot be undone and all data in this site will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSite} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete Site'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilitySiteTabs;
